import { alpha, useTheme } from '@mui/material';
import { StatusItemKDS } from '../interfaces/IKDS';

/**
 * Tokens visuais dos painéis de cozinha.
 *
 * Antes cada tela do KDS escrevia o próprio hexadecimal (`#121212`, `#1e1e1e`,
 * `#2e7d32`, `#e65100`…). Isso quebrava o white-label: o estabelecimento
 * personaliza a paleta em Configurações → Tema, e a cozinha continuava com as
 * cores da Space Food — além de ignorar o botão de tema claro/escuro.
 *
 * Tudo aqui deriva de `theme.palette`, então:
 *   - troca de tema (claro/escuro) e tema do cliente valem também na cozinha;
 *   - a semântica fica explícita: em preparo é `primary`, pronto é `success`,
 *     atrasado é `error` — e não um laranja qualquer.
 *
 * A cor da estação (`estacao.cor`) continua vindo do cadastro: ela identifica a
 * praça de trabalho e é escolhida pelo próprio estabelecimento.
 */
export const useKdsTokens = () => {
    const theme = useTheme();
    const { palette } = theme;

    /** Cor semântica de cada status de item. */
    const statusCor = (status: StatusItemKDS): string => {
        switch (status) {
            case StatusItemKDS.PRONTO:
                return palette.success.main;
            case StatusItemKDS.EM_PREPARO:
                return palette.primary.main;
            case StatusItemKDS.AGUARDANDO_DISPARO:
                return palette.warning.main;
            case StatusItemKDS.AGUARDANDO:
                return palette.text.disabled;
            default:
                return palette.text.disabled;
        }
    };

    /** Cor do cronômetro conforme os alertas de tempo da estação. */
    const alertaCor = (segundos: number, amarelo: number, vermelho: number): string => {
        if (segundos >= vermelho) return palette.error.main;
        if (segundos >= amarelo) return palette.warning.main;
        return palette.success.main;
    };

    /**
     * Cor do contexto do pedido. Delivery e mesa saem da marca do cliente;
     * retirada usa o verde de sucesso, que já significa "pronto para sair".
     */
    const tipoPedidoCor = (tipo?: string): string => {
        switch (tipo) {
            case 'delivery':
                return palette.primary.main;
            case 'mesa':
                return palette.secondary.main;
            case 'retirada':
                return palette.success.main;
            default:
                return palette.text.secondary;
        }
    };

    return {
        statusCor,
        alertaCor,
        tipoPedidoCor,
        /** Fundo do painel em tela cheia. */
        painel: palette.background.default,
        /** Superfície dos cards de pedido. */
        card: palette.background.paper,
        /** Faixa de cabeçalho dentro do card e realces sutis. */
        superficie: alpha(palette.text.primary, 0.04),
        borda: palette.divider,
        texto: palette.text.primary,
        textoSecundario: palette.text.secondary,
        textoFraco: palette.text.disabled,
        /** Realce do item/pedido selecionado pela navegação por teclado. */
        selecao: palette.primary.main,
        selecaoFundo: alpha(palette.primary.main, 0.12),
        /** Fundo tênue de uma cor — usado em pílulas e linhas destacadas. */
        tenue: (cor: string, opacidade = 0.12) => alpha(cor, opacidade),
        palette,
    };
};

export type KdsTokens = ReturnType<typeof useKdsTokens>;
